"use client";

import { Title } from "./Title";
import { Text } from "./Text";
import { ButtonGroup } from "./ButtonGroup";
import { Button, ButtonProps } from "./Button";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import {
  TextDefaultProps,
  TitlePropsWithVariant,
  WithoutMotionProps,
} from "./global";
import { motion } from "motion/react";

const TextMotion = motion(Text);
const TitleMotion = motion(Title);
const ButtonGroupMotion = motion(ButtonGroup);

export type TitleHalfProps = {
  mode?: "default" | "inverted" | "dark";
  tag?: React.ReactNode | null;
  title: TitlePropsWithVariant | null;
  text?: TextDefaultProps | null;
  buttons?: ButtonProps[];
  buttonTrackingClick?: (props: { buttonIndex: number }) => void;
  onButtonClick?: () => void;
} & WithoutMotionProps<Omit<ComponentProps<"div">, "title">>;

export function TitleHalf({
  mode,
  tag,
  title,
  text,
  buttons,
  className,
  buttonTrackingClick,
  onButtonClick = () => undefined,
  ...props
}: TitleHalfProps) {
  const animationEasing = [0.01, 0.0, 0.2, 1.0];
  const animationDuration = 0.8;
  const staggerOffset = 0.2;
  const distance = 30;

  const viewportOptions = {
    once: true,
    margin: "-10% 0px -10% 0px",
  };

  return (
    <div
      className={cn(
        "ui:flex ui:flex-col ui:gap-medium ui:pr-containerTextBox",
        className,
      )}
      {...props}
    >
      <div className={"ui:flex ui:flex-col ui:gap-tiny"}>
        {tag && (
          <TextMotion
            variant={"micro"}
            initial={{ opacity: 0, y: distance }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportOptions}
            transition={{
              duration: animationDuration,
              ease: animationEasing,
            }}
          >
            {tag}
          </TextMotion>
        )}

        {title && (
          <TitleMotion
            variant={"subtitle"}
            {...title}
            initial={{ opacity: 0, y: distance }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportOptions}
            transition={{
              duration: animationDuration,
              ease: animationEasing,
            }}
          />
        )}
      </div>

      {/* TODO: animation removed to keep simpleRichText always visible — restore when a non-hiding animation is designed */}
      {text && (
        <Text
          variant={"body"}
          {...text}
          color={mode === "dark" ? "inverted" : text.color || "light"}
          opacity={mode === "dark" ? 70 : text.opacity}
        />
      )}

      {buttons && buttons.length > 0 && (
        <ButtonGroupMotion
          initial={{ opacity: 0, y: distance }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewportOptions}
          transition={{
            duration: animationDuration,
            ease: animationEasing,
            delay: tag || title ? staggerOffset : 0,
          }}
        >
          {buttons.map((button, index) => (
            <Button
              key={index}
              variant={mode === "inverted" ? "inverted" : undefined}
              {...button}
              onClick={() => {
                buttonTrackingClick?.({
                  buttonIndex: index,
                });
                onButtonClick?.();
              }}
            />
          ))}
        </ButtonGroupMotion>
      )}
    </div>
  );
}
